import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Check, MailOpen, UserPlus, FileCheck2, FileX2, FileText, Sparkles, MailCheck, MousePointerClick, MailWarning } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";

const typeIcon = (t) => {
  switch (t) {
    case "quote_opened": return MailOpen;
    case "quote_accepted": return FileCheck2;
    case "quote_rejected": return FileX2;
    case "quote_revision": return FileText;
    case "quote_clicked": return MousePointerClick;
    case "quote_delivered": return MailCheck;
    case "email_bounced": return MailWarning;
    case "new_customer": return UserPlus;
    case "test": return Sparkles;
    default: return Bell;
  }
};

const typeTone = (t) => {
  switch (t) {
    case "quote_opened": return { bg: "#eff6ff", fg: "#1d4ed8" };
    case "quote_accepted": return { bg: "#ecfdf5", fg: "#047857" };
    case "quote_rejected": return { bg: "#fef2f2", fg: "#b91c1c" };
    case "quote_revision": return { bg: "#fff7ed", fg: "#c2410c" };
    case "quote_clicked": return { bg: "#e0f2fe", fg: "#0369a1" };
    case "quote_delivered": return { bg: "#ecfeff", fg: "#0e7490" };
    case "email_bounced": return { bg: "#fef2f2", fg: "#b91c1c" };
    case "new_customer": return { bg: "#f5f3ff", fg: "#6d28d9" };
    case "test": return { bg: "#fef9c3", fg: "#854d0e" };
    default: return { bg: "#f1f5f9", fg: "#334155" };
  }
};

const fmtRelative = (iso) => {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

export default function NotificationBell({ onDark = true }) {
  const { items, unread, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", h);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", h); window.removeEventListener("keydown", esc); };
  }, [open]);

  const onClickItem = async (n) => {
    if (!n.is_read) await markRead(n.id);
    setOpen(false);
    if (n.quote_id) nav(`/dashboard/quotes/${n.quote_id}`);
    else if (n.meta?.customer_id) nav(`/dashboard/customers/${n.meta.customer_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
          onDark
            ? "bg-white/15 hover:bg-white/25 brand-banner-text border border-white/20"
            : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
        }`}
      >
        <Bell className="w-4 h-4" strokeWidth={2.4} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
          data-testid="notif-dropdown"
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="font-display font-extrabold text-sm text-slate-900">Notifications</div>
              <div className="text-[11px] text-slate-500">{unread > 0 ? `${unread} unread` : "You're all caught up"}</div>
            </div>
            <button
              onClick={markAllRead}
              disabled={unread === 0}
              data-testid="notif-mark-all"
              className="text-[11px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-700 disabled:text-slate-300 inline-flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-xs text-slate-500">No notifications yet.</div>
            )}
            {items.map((n) => {
              const Icon = typeIcon(n.type);
              const tone = typeTone(n.type);
              return (
                <button
                  key={n.id}
                  onClick={() => onClickItem(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                    !n.is_read ? "bg-blue-50/40" : ""
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-bold text-slate-900 truncate">{n.title}</div>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" aria-label="unread" />}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{n.message}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{fmtRelative(n.created_at)}</div>
                  </div>
                  {!n.is_read && (
                    <span
                      className="text-[10px] text-blue-600 font-bold uppercase tracking-wider self-start opacity-0 group-hover:opacity-100"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-right">
            <Link
              to="/dashboard/notifications"
              onClick={() => setOpen(false)}
              data-testid="notif-see-all"
              className="text-[11px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-700"
            >
              See all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
